///
/// EarthBrowser
///
/// Created by Matt Giger on 7/12/2024
/// Copyright (c) 2024 Geodata Labs
///

import { PageContent } from "@/components/controls/PageContent"
import { SignInHeaderButton } from "@/components/header/SignInHeaderButton"
import { PageMeta } from "@/components/shared/PageMeta"
import { Button } from "@/components/ui/button"
import { Loader } from "@/components/ui/loader"
import { getBetaStatus } from "@/hooks/useBetaStatus"
import { useOnboarding } from "@/hooks/useOnboarding"
import { useShareLaunch } from "@/hooks/useShareLaunch"
import { setBetaTestOpen } from "@/redux/dialogSlice"
import { useAppDispatch } from "@/redux/store"
import { useGetUserProfileQuery } from "@/redux/userApi"
import { Compass, Globe, Sparkles, Stars, Telescope } from "lucide-react"

const { isBeta } = getBetaStatus()

export default function Page() {
	const { data, isLoading } = useGetUserProfileQuery()

	// A share link (?s=) is resolved into ?lat/?lon/?layers=/?target= before
	// the app mounts, so the engine's launch deep-link handling sees it
	const shareResolved = useShareLaunch()

	// Automatically show onboarding for new users
	useOnboarding(data)

	// During beta, signed-out visitors see the coming-soon screen. Outside beta
	// the app itself is public (breadth free, depth paid) — render it for
	// everyone and let AccountMenu handle the signed-out state.
	const showApp = !isBeta || data !== undefined

	return (
		<div className="w-full h-full overflow-y-auto">
			<PageMeta path="/" />
			{ !shareResolved
				? <Loader/>
				: showApp
					? <PageContent/>
					: (isLoading
						? <Loader/>
						: <ComingSoonContent/>)
			}
		</div>
	)
}

function ComingSoonContent() {
	const dispatch = useAppDispatch()

	const handleSignUp = () => {
		dispatch(setBetaTestOpen(true))
	}

	return (
		<div className="h-full overflow-y-auto flex flex-col bg-gradient-to-b from-cyan-50 to-teal-100 dark:from-slate-900 dark:to-slate-800">
			<nav className="flex flex-row justify-end items-center p-4">
				<SignInHeaderButton/>
			</nav>

			<main className="flex-1 flex items-center justify-center px-4 py-12">
				<div className="max-w-2xl w-full text-center">
					{/* Animated Globe Icon with orbiting elements */}
					<div className="mb-8 relative">
						<div className="inline-block">
							<div className="relative">
								<div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 rounded-full blur-2xl opacity-50" />
								<div className="relative animate-spin-slow">
									<Globe className="h-24 w-24 text-cyan-600 dark:text-cyan-400 mx-auto" />
								</div>
							</div>
						</div>
						<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
							<div className="relative w-48 h-48">
								<div className="absolute top-0 left-1/2 -translate-x-1/2 animate-pulse-scale">
									<Telescope className="h-8 w-8 text-blue-500 dark:text-blue-400" />
								</div>
								<div className="absolute bottom-0 right-4 animate-pulse-scale-reverse">
									<Compass className="h-6 w-6 text-teal-600 dark:text-teal-400" />
								</div>
								<div className="absolute top-1/2 left-0 -translate-y-1/2 animate-pulse-scale">
									<Stars className="h-6 w-6 text-purple-500 dark:text-purple-400" />
								</div>
							</div>
						</div>
					</div>

					{/* Main Message */}
					<div className="space-y-4 mb-8">
						<h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
							The Journey Begins Soon
						</h1>
						<p className="text-xl text-gray-600 dark:text-gray-300">
							We&apos;re charting new territories in Earth exploration...
						</p>
						<div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
							<Sparkles className="h-5 w-5 text-cyan-500" />
							<p className="text-sm">
								Something extraordinary is being prepared for you
							</p>
						</div>
					</div>

					{/* Fun Messages Card */}
					<div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-2xl p-6 shadow-xl mb-8 border border-cyan-200 dark:border-slate-700">
						<p className="text-gray-700 dark:text-gray-300 mb-4">
							We&apos;re crafting an experience that will let you explore our planet like never before.
							Want to be among the first explorers?
						</p>
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
							<div className="flex items-center justify-center gap-2">
								<span className="text-2xl">🌍</span>
								<span>Discover the Earth</span>
							</div>
							<div className="flex items-center justify-center gap-2">
								<span className="text-2xl">🔭</span>
								<span>Explore with purpose</span>
							</div>
							<div className="flex items-center justify-center gap-2">
								<span className="text-2xl">✨</span>
								<span>Join the adventure</span>
							</div>
						</div>
					</div>

					{/* Call to Action */}
					<div className="space-y-4">
						<h2 className="text-2xl font-bold bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
							Request Early Access
						</h2>
						<Button
							size="lg"
							onClick={handleSignUp}
							className="rounded-full shadow-lg transition-all duration-300 active:scale-95 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
						>
							<Sparkles className="h-5 w-5 mr-2" />
							Sign Up for Beta Access
						</Button>
					</div>

					{/* Status Code (playful) */}
					<p className="mt-8 text-xs text-gray-400 dark:text-gray-600 font-mono">
						STATUS: PRE_LAUNCH_SEQUENCE_INITIATED
					</p>
				</div>
			</main>
		</div>
	)
}